import { MesfModal } from "@dexteel/mesf-core";
import SearchIcon from "@mui/icons-material/Search";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
	alpha,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	InputAdornment,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { AREA_COLOR_MAP } from "@/shared/colors";
import {
	ALL_PRODUCT_AREAS,
	getProductArea,
	PRODUCT_AREA_ASSET_ID,
	PRODUCT_AREA_LABEL,
	Product,
	ProductArea,
} from "@/shared/models/Product";

type Props = {
	open: boolean;
	title: string;
	products: Product[];
	/** When true, warn before selecting a product that already has a parent. */
	checkParentConflict?: boolean;
	/** Areas to show as unchecked & disabled, with tooltip text per area. */
	disabledAreas?: Partial<Record<Exclude<ProductArea, null>, string>>;
	onSelect: (productId: number) => void;
	onClose: () => void;
};

const ALL_AREAS: Exclude<ProductArea, null>[] = [...ALL_PRODUCT_AREAS];

export const ProductPickerModal = ({
	open,
	title,
	products,
	checkParentConflict,
	disabledAreas,
	onSelect,
	onClose,
}: Props) => {
	const [search, setSearch] = useState("");
	const [enabledAreas, setEnabledAreas] = useState<
		Set<Exclude<ProductArea, null>>
	>(new Set(ALL_AREAS));
	const [confirmCandidate, setConfirmCandidate] = useState<Product | null>(
		null,
	);

	useEffect(() => {
		if (open) {
			setSearch("");
			setEnabledAreas(new Set(ALL_AREAS.filter((a) => !disabledAreas?.[a])));
			setConfirmCandidate(null);
		}
	}, [open, disabledAreas]);

	const handleRowClick = (p: Product) => {
		if (p.ProductId == null) return;
		if (checkParentConflict && p.ParentProductId != null) {
			setConfirmCandidate(p);
			return;
		}
		onSelect(p.ProductId);
	};

	const toggleArea = (a: Exclude<ProductArea, null>) => {
		setEnabledAreas((prev) => {
			const next = new Set(prev);
			if (next.has(a)) next.delete(a);
			else next.add(a);
			return next;
		});
	};

	const filtered = useMemo(() => {
		const q = search.trim().toLowerCase();
		return products.filter((p) => {
			const a = getProductArea(p);
			if (a == null) return enabledAreas.size === ALL_AREAS.length;
			if (!enabledAreas.has(a)) return false;
			if (!q) return true;
			return (
				p.PartNumber.toLowerCase().includes(q) ||
				(p.CustomerName ?? "").toLowerCase().includes(q) ||
				(p.SectionName ?? "").toLowerCase().includes(q) ||
				(p.GradeName ?? "").toLowerCase().includes(q)
			);
		});
	}, [products, search, enabledAreas]);

	return (
		<>
			<MesfModal
				id="modal-product-picker"
				title={title}
				open={open}
				handleClose={onClose}
				maxWidth="md"
			>
				<MesfModal.Content style={{ padding: "15px 24px" }}>
					<Stack
						direction="row"
						spacing={1}
						sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
					>
						{ALL_AREAS.map((a) => {
							const disabledTooltip = disabledAreas?.[a];
							const isDisabled = !!disabledTooltip;
							const on = !isDisabled && enabledAreas.has(a);
							const color = AREA_COLOR_MAP[PRODUCT_AREA_ASSET_ID[a]];

							const chip = (
								<Box
									key={a}
									onClick={isDisabled ? undefined : () => toggleArea(a)}
									sx={{
										display: "inline-flex",
										alignItems: "center",
										pl: 0.5,
										pr: 1.25,
										py: 0.25,
										borderRadius: 1,
										border: "1px solid",
										borderColor: on ? color : "divider",
										backgroundColor: on ? alpha(color, 0.08) : "transparent",
										cursor: isDisabled ? "default" : "pointer",
										userSelect: "none",
										opacity: isDisabled ? 0.4 : 1,
										transition: "all 120ms ease",
										...(!isDisabled && {
											"&:hover": {
												backgroundColor: on
													? alpha(color, 0.14)
													: "action.hover",
												borderColor: on ? color : "text.secondary",
											},
										}),
									}}
								>
									<FormControlLabel
										control={
											<Checkbox
												checked={on}
												disabled={isDisabled}
												size="small"
												disableRipple
												sx={{
													p: 0.5,
													color: color,
													"&.Mui-checked": { color: color },
												}}
											/>
										}
										label={PRODUCT_AREA_LABEL[a]}
										sx={{
											m: 0,
											pointerEvents: "none",
											"& .MuiFormControlLabel-label": {
												fontSize: "0.78rem",
												fontWeight: 700,
												letterSpacing: "0.04em",
												textTransform: "uppercase",
												color: on ? color : "text.secondary",
												ml: 0.25,
											},
										}}
									/>
								</Box>
							);

							return isDisabled ? (
								<Tooltip key={a} title={disabledTooltip} arrow placement="top">
									{chip}
								</Tooltip>
							) : (
								chip
							);
						})}
					</Stack>

					<TextField
						fullWidth
						size="small"
						autoFocus
						placeholder="Search part number, customer, section, grade…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon
										fontSize="small"
										sx={{ color: "text.disabled" }}
									/>
								</InputAdornment>
							),
						}}
						sx={{ mb: 2 }}
					/>

					<Box
						sx={{
							height: 420,
							overflow: "auto",
							border: "1px solid",
							borderColor: "divider",
							borderRadius: 1,
						}}
					>
						{filtered.length === 0 ? (
							<Typography
								color="text.secondary"
								sx={{ py: 5, textAlign: "center" }}
							>
								No products match the current filters
							</Typography>
						) : (
							filtered.map((p) => {
								const a = getProductArea(p);
								const color =
									a != null
										? AREA_COLOR_MAP[PRODUCT_AREA_ASSET_ID[a]]
										: "#9e9e9e";
								const areaLabel = a != null ? PRODUCT_AREA_LABEL[a] : "—";
								const secondary = [p.CustomerName, p.SectionName, p.GradeName]
									.filter(Boolean)
									.join("  ·  ");
								const showParentWarning =
									checkParentConflict &&
									p.ParentProductId != null &&
									p.ParentPartNumber;
								return (
									<Box
										key={p.ProductId}
										onClick={() => handleRowClick(p)}
										sx={{
											display: "flex",
											alignItems: "center",
											gap: 1.5,
											px: 1.5,
											py: 1,
											cursor: "pointer",
											borderBottom: "1px solid",
											borderColor: "divider",
											"&:last-child": { borderBottom: "none" },
											"&:hover": { backgroundColor: "action.hover" },
										}}
									>
										<Box
											sx={{
												px: 1,
												py: 0.35,
												borderRadius: 0.75,
												backgroundColor: color,
												color: "#fff",
												fontSize: "0.58rem",
												fontWeight: 700,
												textTransform: "uppercase",
												letterSpacing: "0.05em",
												minWidth: 48,
												textAlign: "center",
												flexShrink: 0,
											}}
										>
											{areaLabel}
										</Box>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography
												fontWeight={700}
												sx={{
													fontSize: "0.9rem",
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}}
											>
												{p.PartNumber}
											</Typography>
											{secondary && (
												<Typography
													variant="caption"
													color="text.secondary"
													sx={{
														display: "block",
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
													}}
												>
													{secondary}
												</Typography>
											)}
										</Box>
										{showParentWarning && (
											<Typography
												component="div"
												variant="caption"
												sx={{
													flexShrink: 0,
													display: "flex",
													alignItems: "center",
													gap: 0.5,
													pl: 1.5,
													color: "warning.main",
													fontStyle: "italic",
													whiteSpace: "nowrap",
												}}
											>
												<WarningAmberIcon sx={{ fontSize: "0.95rem" }} />
												Currently child of {p.ParentPartNumber}
											</Typography>
										)}
									</Box>
								);
							})
						)}
					</Box>

					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ mt: 1, display: "block" }}
					>
						{filtered.length} of {products.length} products
					</Typography>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "16px 24px" }}>
					<Button
						fullWidth
						variant="contained"
						color="inherit"
						onClick={onClose}
					>
						Cancel
					</Button>
				</MesfModal.Actions>
			</MesfModal>

			<ConfirmationDialogRaw
				open={confirmCandidate !== null}
				title="Replace existing parent?"
				message={
					confirmCandidate
						? `'${confirmCandidate.PartNumber}' is currently a child of '${confirmCandidate.ParentPartNumber}'. Linking it here will disconnect it from '${confirmCandidate.ParentPartNumber}'. Continue?`
						: undefined
				}
				onClose={(value) => {
					const target = confirmCandidate;
					setConfirmCandidate(null);
					if (value === "OK" && target?.ProductId != null) {
						onSelect(target.ProductId);
					}
				}}
			/>
		</>
	);
};
